import * as THREE from 'three/webgpu';

import { CREATURE as CREATURE_BAKE } from '../bake/BakeConfig.js';
import { OccluderSet } from '../bake/Occluders.js';
import { applyBake, bakeSync } from '../bake/bakeVertexLight.js';
import { triCount } from '../build/mergeGeo.js';

/**
 * @file PROCEDURAL CREATURES: built in code from three.js primitives, animated
 * by their own JS. This is the registry + the type builder — the authored cast
 * lives in `src/game/creatures/` and registers itself here, exactly the way art
 * kinds register with `enemyArt.js`. Adding a creature never edits an engine
 * file.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE MODEL: a creature is a bag of rigid PARTS
 * ─────────────────────────────────────────────────────────────────────────────
 * Three separable pieces per creature (see HANDOFF-RIG.md §3):
 *
 *  • `catalogue(params)` — the SHARED part geometries, built once per type.
 *    Every geometry must be non-indexed, flat-shaded, and carry a linear-albedo
 *    `color` attribute (use {@link tintAlbedo}); base conventions follow
 *    `build/prims.js` (author at a comfortable scale — `worldHeight` rescales).
 *  • `build(params)` — which parts THIS type has: an array of {@link part}
 *    placements. Per-type customization (a turret variant, an optional weapon)
 *    lives here, driven by the def's `params`.
 *  • `pose(t, p)` — OPTIONAL, a PURE function of the walk phase `t` (radians,
 *    0..TAU) mutating the part nodes in `p`. Purity is load-bearing: a pure
 *    `pose` is sampling-ready by construction, so a GPU-posed path can be added
 *    later under this exact API (HANDOFF-RIG §2.1). No `Math.random`, no
 *    reading game state, no memory between calls.
 *  • `update(dt, u, p)` — OPTIONAL, arbitrary: sees a per-unit view `u`
 *    (position, facing, live target, persistent `u.mem`) and may do anything
 *    to the nodes. Runs AFTER `pose` and after the gait blend, so a turret
 *    keeps tracking while the hull stands still.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT `buildRig` DOES (per TYPE, at load — never per frame)
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. Builds the catalogue, clones one geometry per part SLOT (mirrored slots
 *    get a winding-safe X-flip — never a negative scale, see CLAUDE.md).
 * 2. Assembles the rest pose in creature space, measures it, and rescales so
 *    the creature stands `worldHeight` tall, feet at y = 0, centred on XZ.
 *    The uniform scale is folded into the part geometries and rest offsets, so
 *    the runtime never multiplies an extra matrix.
 * 3. Bakes EVERY part in rest pose with the WHOLE creature as the occluder set
 *    (inter-part AO), then carries the two baked channels back onto the
 *    part-LOCAL geometry — so pose rotations pivot at the part origin and a
 *    swinging arm keeps its rest-pose shading, the same trade the engine makes
 *    everywhere.
 *
 * Draw calls = number of part SLOTS per type (each slot bakes differently — a
 * left wing shades differently from a right one), NOT number of units:
 * `RigEnemyRenderer` batches every unit sharing a slot geometry into one draw.
 *
 * A "part" is a rigid group that moves INDEPENDENTLY. The tank's hull, tracks
 * and back shards merge into ONE part; give a creature only as many parts as
 * its animation needs. {@link MAX_PARTS} is the hard fence.
 *
 * @module engine/actors/creatures
 */

/** @typedef {import('../types.js').EnemyRigBackend} EnemyRigBackend */

/**
 * Hard cap on part slots per creature. The full roster budget is ~15–25 draws
 * (vs ~60 for the whole city), so a single 17-part creature is a design error,
 * not a bigger budget.
 */
export const MAX_PARTS = 16;
/** Above this, warn — most creatures should be 1–6 parts. */
const SOFT_PARTS = 8;

/** @type {Map<string, CreatureDef>} */
const CREATURES = new Map();

/**
 * @typedef {Object} CreatureDef
 * @property {(params: Object) => Object<string, THREE.BufferGeometry>} catalogue
 * @property {(params: Object) => PartPlacement[]} build
 * @property {((t: number, p: Object<string, PartNode>) => void)|null} [pose]
 * @property {((dt: number, u: RigUnitView, p: Object<string, PartNode>) => void)|null} [update]
 */

/**
 * One part placement produced by {@link part}.
 * @typedef {Object} PartPlacement
 * @property {string} key  Catalogue key.
 * @property {string} name Node name `pose`/`update` address (`p.wingL`).
 * @property {number} x @property {number} y @property {number} z
 * @property {number} rx @property {number} ry @property {number} rz
 * @property {boolean} mirror
 */

/**
 * The mutable node `pose`/`update` write to. Reset to the rest transform every
 * frame before `pose` runs, so poses are absolute, not accumulative.
 * @typedef {Object} PartNode
 * @property {{x:number,y:number,z:number}} position Metres, creature space.
 * @property {{x:number,y:number,z:number}} rotation Euler XYZ, radians.
 * @property {{x:number,y:number,z:number}} scale
 */

/**
 * What `update` sees of its unit. Everything here is a copy/view — mutating it
 * (except `mem`) changes nothing.
 * @typedef {Object} RigUnitView
 * @property {number} x @property {number} z World position.
 * @property {number} yaw Facing, radians.
 * @property {number} state FSM state byte (`actors/ai.js#STATE`).
 * @property {boolean} hasTarget
 * @property {number} targetX @property {number} targetZ Valid iff `hasTarget`.
 * @property {Object} mem Persistent per-unit scratch (reset on spawn). The ONLY
 *   place `update` may keep state between frames.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Registers a creature generator. Call at module scope in
 * `src/game/creatures/<id>.js`; the manifest (`index.js` there) importing the
 * module IS the registration.
 *
 * @param {string} id Stable slug — what a def's `art.rig.generator` names.
 * @param {CreatureDef} def
 * @returns {CreatureDef} `def`, so `export default registerCreature(...)` works.
 */
export function registerCreature(id, def) {
  if (!id || typeof id !== 'string') throw new Error('registerCreature: an id is required');
  if (typeof def?.catalogue !== 'function' || typeof def?.build !== 'function') {
    throw new Error(`registerCreature(${id}): need { catalogue, build } functions`);
  }
  if (def.pose && typeof def.pose !== 'function') throw new Error(`registerCreature(${id}): pose must be a function`);
  if (def.update && typeof def.update !== 'function') throw new Error(`registerCreature(${id}): update must be a function`);
  if (CREATURES.has(id)) console.warn(`[creatures] "${id}" registered twice — last one wins`);
  CREATURES.set(id, def);
  return def;
}

/** @returns {string[]} Registered generator ids, registration order. */
export function creatureIds() {
  return [...CREATURES.keys()];
}

/**
 * @param {string} id
 * @returns {CreatureDef|null}
 */
export function getCreature(id) {
  return CREATURES.get(id) ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Authoring helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Declares one part placement inside `build()`.
 *
 * @param {string} key Catalogue key.
 * @param {Object} [o]
 * @param {string} [o.name=key] Node name for `pose`/`update`. REQUIRED to be
 *   unique when a key is placed twice (`part('wing', {name: 'wingL', ...})`).
 * @param {number} [o.x=0] @param {number} [o.y=0] @param {number} [o.z=0]
 *   Rest offset from the creature origin, authoring units.
 * @param {number} [o.rx=0] @param {number} [o.ry=0] @param {number} [o.rz=0]
 *   Rest rotation, Euler XYZ radians.
 * @param {boolean} [o.mirror=false] X-flips the GEOMETRY (winding-safe — never
 *   a negative scale, which back-face culling would eat; see CLAUDE.md).
 * @returns {PartPlacement}
 */
export function part(key, o = {}) {
  return {
    key,
    name: o.name ?? key,
    x: o.x ?? 0,
    y: o.y ?? 0,
    z: o.z ?? 0,
    rx: o.rx ?? 0,
    ry: o.ry ?? 0,
    rz: o.rz ?? 0,
    mirror: !!o.mirror,
  };
}

/**
 * Writes a flat linear-albedo `color` attribute onto a part geometry — the
 * bake's input. Take the triple from `levels/shared/palette.js#hexToLinear`
 * (the canonical hex → linear helper), never from a hand-parsed hex: raw sRGB
 * used as linear is ~2.2x too bright and invisible until it is on screen.
 *
 * @param {THREE.BufferGeometry} geo
 * @param {[number, number, number]} rgb LINEAR triple.
 * @returns {THREE.BufferGeometry} `geo`, for chaining.
 */
export function tintAlbedo(geo, rgb) {
  const n = geo.getAttribute('position').count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = rgb[0];
    arr[i * 3 + 1] = rgb[1];
    arr[i * 3 + 2] = rgb[2];
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return geo;
}

// ─────────────────────────────────────────────────────────────────────────────
// The type builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the rig backend for one enemy TYPE. Deterministic: same generator +
 * params ⇒ byte-identical geometry (creature authors take no rng — rule 4).
 *
 * @param {string} generator Registered creature id.
 * @param {Object} [o]
 * @param {Object} [o.params] Forwarded to `catalogue` and `build`.
 * @param {number} [o.worldHeight=1.4] Metres — authoritative, like every art
 *   kind: the assembled rest pose is rescaled to stand exactly this tall.
 * @returns {EnemyRigBackend}
 */
export function buildRig(generator, o = {}) {
  const def = CREATURES.get(generator);
  if (!def) {
    throw new Error(`buildRig: unknown creature "${generator}" — is src/game/creatures/${generator}.js in the manifest?`);
  }
  const params = o.params ?? {};
  const worldHeight = o.worldHeight > 0 ? o.worldHeight : 1.4;

  // ── 1. catalogue + placements ──────────────────────────────────────────────
  const catalogue = def.catalogue(params);
  if (!catalogue || typeof catalogue !== 'object' || Object.keys(catalogue).length === 0) {
    throw new Error(`creature ${generator}: catalogue() returned nothing`);
  }
  for (const [key, geo] of Object.entries(catalogue)) {
    if (geo?.index !== null && geo?.index !== undefined) {
      throw new Error(`creature ${generator}: catalogue "${key}" is indexed — parts must be non-indexed (rule 3)`);
    }
    if (!geo?.getAttribute?.('position')) throw new Error(`creature ${generator}: catalogue "${key}" has no position`);
    if (!geo.getAttribute('normal')) throw new Error(`creature ${generator}: catalogue "${key}" has no normals`);
    if (!geo.getAttribute('color')) {
      throw new Error(`creature ${generator}: catalogue "${key}" has no albedo — tintAlbedo() it`);
    }
  }

  const placements = def.build(params);
  if (!Array.isArray(placements) || placements.length === 0) {
    throw new Error(`creature ${generator}: build() placed no parts`);
  }
  if (placements.length > MAX_PARTS) {
    throw new Error(`creature ${generator}: ${placements.length} parts > MAX_PARTS ${MAX_PARTS} — merge rigid groups`);
  }
  if (placements.length > SOFT_PARTS) {
    console.warn(`[creatures] ${generator}: ${placements.length} parts — every part is a draw call, merge what moves together`);
  }
  const seen = new Set();
  for (const pl of placements) {
    if (!catalogue[pl.key]) throw new Error(`creature ${generator}: build() places unknown key "${pl.key}"`);
    if (seen.has(pl.name)) throw new Error(`creature ${generator}: duplicate part name "${pl.name}" — name mirrored/repeated parts`);
    seen.add(pl.name);
  }

  // ── 2. per-slot local geometry + rest-pose measurement ─────────────────────
  const euler = new THREE.Euler();
  const rest = new THREE.Matrix4();
  /** @type {Array<{pl: PartPlacement, local: THREE.BufferGeometry}>} */
  const slots = placements.map((pl) => ({
    pl,
    local: pl.mirror ? mirrorX(catalogue[pl.key]) : catalogue[pl.key].clone(),
  }));

  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  box.makeEmpty();
  for (const s of slots) {
    const cs = s.local.clone();
    cs.applyMatrix4(restMatrix(rest, euler, s.pl, 1, 0, 0, 0));
    cs.computeBoundingBox();
    box.union(tmp.copy(/** @type {THREE.Box3} */ (cs.boundingBox)));
    cs.dispose();
  }
  const height = box.max.y - box.min.y;
  const scale = height > 1e-6 ? worldHeight / height : 1;
  const tx = -((box.min.x + box.max.x) / 2) * scale;
  const ty = -box.min.y * scale;
  const tz = -((box.min.z + box.max.z) / 2) * scale;

  // Fold the uniform scale into the geometry and the rest offsets — uniform
  // scale commutes with rotation, so the runtime matrix stays a rigid TRS.
  for (const s of slots) if (scale !== 1) s.local.scale(scale, scale, scale);

  // ── 3. occluders (whole creature, rest pose) + per-part bake ───────────────
  const occ = new OccluderSet();
  /** @type {THREE.BufferGeometry[]} */
  const csGeos = [];
  for (const s of slots) {
    const cs = s.local.clone();
    cs.applyMatrix4(restMatrix(rest, euler, s.pl, scale, tx, ty, tz));
    cs.computeBoundingBox();
    const bb = /** @type {THREE.Box3} */ (cs.boundingBox);
    occ.addBox({ x: bb.min.x, y: bb.min.y, z: bb.min.z }, { x: bb.max.x, y: bb.max.y, z: bb.max.z });
    csGeos.push(cs);
  }
  occ.build({ cell: 0.5 });

  let tris = 0;
  const parts = slots.map((s, i) => {
    const cs = csGeos[i];
    // Bake in creature space (positions/normals matter to AO + N·L), then carry
    // the two channels back to the LOCAL geometry — same vertex order, so the
    // arrays transplant directly. `color` on the local geo held albedo; after
    // this it holds the day channel, per the project-wide convention.
    applyBake(s.local, bakeSync(cs, occ, null, CREATURE_BAKE));
    cs.dispose();
    s.local.computeBoundingBox();
    s.local.computeBoundingSphere();
    const t = triCount(s.local);
    tris += t;
    return {
      name: s.pl.name,
      key: s.pl.key,
      geometry: s.local,
      tris: t,
      rest: {
        x: s.pl.x * scale + tx,
        y: s.pl.y * scale + ty,
        z: s.pl.z * scale + tz,
        rx: s.pl.rx,
        ry: s.pl.ry,
        rz: s.pl.rz,
      },
    };
  });

  for (const geo of Object.values(catalogue)) geo.dispose();

  let disposed = false;
  return {
    generator,
    parts,
    pose: def.pose ?? null,
    update: def.update ?? null,
    partCount: parts.length,
    tris,
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const p of parts) p.geometry?.dispose?.();
    },
  };
}

/**
 * Wraps ONE already-baked geometry as a single-part rig — how the placeholder
 * (and anything else engine-side) joins the rig path without registering a
 * generator.
 *
 * @param {THREE.BufferGeometry} geometry Non-indexed, `color`+`color1` baked,
 *   feet at y = 0.
 * @param {{name?: string, generator?: string}} [o]
 * @returns {EnemyRigBackend}
 */
export function rigFromGeometry(geometry, o = {}) {
  let disposed = false;
  return {
    generator: o.generator ?? 'static',
    parts: [
      {
        name: o.name ?? 'body',
        key: o.name ?? 'body',
        geometry,
        tris: triCount(geometry),
        rest: { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0 },
      },
    ],
    pose: null,
    update: null,
    partCount: 1,
    tris: triCount(geometry),
    dispose() {
      if (disposed) return;
      disposed = true;
      geometry?.dispose?.();
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rest matrix for a placement: `T(pos*scale + t) · R(euler)`. Scale is NOT in
 * the matrix — it is folded into the geometry (see the caller).
 *
 * @param {THREE.Matrix4} m Scratch. @param {THREE.Euler} e Scratch.
 * @param {PartPlacement} pl
 * @param {number} scale @param {number} tx @param {number} ty @param {number} tz
 * @returns {THREE.Matrix4} `m`
 */
function restMatrix(m, e, pl, scale, tx, ty, tz) {
  m.makeRotationFromEuler(e.set(pl.rx, pl.ry, pl.rz, 'XYZ'));
  m.setPosition(pl.x * scale + tx, pl.y * scale + ty, pl.z * scale + tz);
  return m;
}

/**
 * Winding-safe X mirror: flips positions and normals across X, then reverses
 * each triangle's vertex order so the faces stay front-facing. A negative
 * matrix scale would flip the winding instead and culling would eat the part —
 * the exact trap `SpriteBatch` documents for sprites.
 *
 * @param {THREE.BufferGeometry} src Non-indexed.
 * @returns {THREE.BufferGeometry} A new geometry.
 */
export function mirrorX(src) {
  const geo = src.clone();
  for (const name of ['position', 'normal']) {
    const attr = /** @type {THREE.BufferAttribute} */ (geo.getAttribute(name));
    if (!attr) continue;
    const a = /** @type {Float32Array} */ (attr.array);
    for (let i = 0; i < a.length; i += 3) a[i] = -a[i];
    attr.needsUpdate = true;
  }
  // Reverse winding: swap vertices 1 and 2 of every triangle, on EVERY attribute.
  const count = geo.getAttribute('position').count;
  for (const name of Object.keys(geo.attributes)) {
    const attr = /** @type {THREE.BufferAttribute} */ (geo.getAttribute(name));
    const size = attr.itemSize;
    const a = /** @type {Float32Array} */ (attr.array);
    for (let tri = 0; tri + 2 < count; tri += 3) {
      const i1 = (tri + 1) * size;
      const i2 = (tri + 2) * size;
      for (let k = 0; k < size; k++) {
        const t = a[i1 + k];
        a[i1 + k] = a[i2 + k];
        a[i2 + k] = t;
      }
    }
    attr.needsUpdate = true;
  }
  return geo;
}
